import type { RuntimeDiagnosticsProcess } from "@cmmd-forge/contracts";

import {
  buildProcessTableRows,
  formatCpuPercent,
  formatResidentMemory,
  processCommandLabel,
  processTreePrefix,
  statTone,
} from "./DiagnosticsProcessTable.logic";
import { StatusDot } from "./DiagnosticsPanelPrimitives";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

/**
 * Both measurement columns come from `ps` and both need a caveat, so the
 * caveat lives on the header where it is read once rather than per row.
 */
function MeasurementHeader({
  label,
  explanation,
}: {
  readonly label: string;
  readonly explanation: string;
}) {
  return (
    <th className="px-4 py-2 text-right font-medium">
      <Tooltip>
        <TooltipTrigger
          render={<span className="cursor-help underline decoration-dotted underline-offset-4" />}
        >
          {label}
        </TooltipTrigger>
        <TooltipPopup side="top">{explanation}</TooltipPopup>
      </Tooltip>
    </th>
  );
}

export function DiagnosticsProcessesTable({
  processes,
  rootPid,
  collectionError,
}: {
  readonly processes: ReadonlyArray<RuntimeDiagnosticsProcess>;
  readonly rootPid: number | null;
  /** Why the list is empty, when it is empty because `ps` could not be run. */
  readonly collectionError?: string | undefined;
}) {
  const rows = buildProcessTableRows({ processes, rootPid });
  if (rows.length === 0) {
    // "No process data" against a machine that is plainly running reads as a
    // claim about the machine. It is a claim about this reader's reach, and
    // on a Cloud VM the reason is mundane: the image ships no `procps`.
    return collectionError ? (
      <div className="space-y-1 px-4 py-6 text-sm">
        <p className="text-foreground">This Sprite's process list could not be read.</p>
        <p className="font-mono text-xs text-muted-foreground">{collectionError}</p>
        <p className="text-xs text-muted-foreground">
          Sprite images ship without <code className="font-mono text-xs">ps</code>. Everything else
          on this page is unaffected.
        </p>
      </div>
    ) : (
      <div className="px-4 py-8 text-center text-sm text-muted-foreground">
        No process data has been collected yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead className="text-left text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
          <tr className="border-b border-border/65">
            <th className="px-4 py-2 font-medium">Process</th>
            <th className="px-4 py-2 font-medium">PID</th>
            <th className="px-4 py-2 font-medium">State</th>
            {/* `ps`'s %cpu is lifetime-average on Linux, so the header says
                what it is rather than implying a live reading. */}
            <MeasurementHeader
              label="CPU avg"
              explanation="ps %cpu — averaged over the process lifetime on Linux, recent usage on macOS"
            />
            <MeasurementHeader label="Memory" explanation="Resident set size, from ps rss" />
            <th className="px-4 py-2 font-medium">Command</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={`${row.process.pid}-${row.process.command}`}
              className="border-b border-border/45 last:border-b-0"
            >
              <td className="px-4 py-2.5">
                <span className="flex items-center">
                  {row.depth > 0 ? (
                    <span className="select-none whitespace-pre font-mono text-[11px] text-muted-foreground/30">
                      {processTreePrefix(row)}
                    </span>
                  ) : null}
                  <span className="font-medium text-foreground">
                    {processCommandLabel(row.process)}
                  </span>
                </span>
              </td>
              <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                {row.process.pid}
              </td>
              <td className="px-4 py-2.5">
                <span className="inline-flex items-center gap-1.5">
                  <StatusDot tone={statTone(row.process.stat)} />
                  <span className="font-mono text-xs text-muted-foreground">
                    {row.process.stat}
                  </span>
                </span>
              </td>
              <td className="px-4 py-2.5 text-right font-mono text-xs tabular-nums text-muted-foreground">
                {formatCpuPercent(row.process.cpuPercent)}
              </td>
              <td className="px-4 py-2.5 text-right font-mono text-xs tabular-nums text-muted-foreground">
                {formatResidentMemory(row.process.rssKb)}
              </td>
              <td className="max-w-[520px] truncate px-4 py-2.5 font-mono text-xs text-muted-foreground">
                {row.process.command}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
